import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

interface CanonicalPhrase {
  id: string;
  action_type_id: string;
  phrase_pattern: string;
  match_type: 'exact' | 'contains' | 'regex';
  confidence_threshold: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface Props {
  actionTypeId: string;
  actionTypeLabel: string;
}

const MATCH_TYPE_OPTIONS = [
  { label: 'Contains', value: 'contains', description: 'Text contains this phrase anywhere' },
  { label: 'Exact Match', value: 'exact', description: 'Text exactly matches this phrase' },
  { label: 'Regex', value: 'regex', description: 'Advanced pattern matching with regex' }
];

export default function CanonicalPhraseManager({ actionTypeId, actionTypeLabel }: Props) {
  const { toast } = useToast();
  const [phrases, setPhrases] = useState<CanonicalPhrase[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    phrase_pattern: '',
    match_type: 'contains' as const,
    confidence_threshold: 80,
    enabled: true
  });

  useEffect(() => {
    fetchPhrases();
  }, [actionTypeId]);

  const fetchPhrases = async () => {
    try {
      const { data, error } = await supabase
        .from('canonical_phrases')
        .select('*')
        .eq('action_type_id', actionTypeId)
        .order('confidence_threshold', { ascending: false });

      if (error) throw error;
      setPhrases((data as CanonicalPhrase[]) || []);
    } catch (error) {
      console.error('Error fetching canonical phrases:', error);
      toast({
        title: "Error",
        description: "Failed to load canonical phrases",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const { error } = await supabase
        .from('canonical_phrases')
        .insert([{ ...formData, action_type_id: actionTypeId }]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Canonical phrase created successfully",
      });

      setIsCreateDialogOpen(false);
      setFormData({
        phrase_pattern: '',
        match_type: 'contains',
        confidence_threshold: 80,
        enabled: true
      });
      fetchPhrases();
    } catch (error) {
      console.error('Error creating canonical phrase:', error);
      toast({
        title: "Error",
        description: "Failed to create canonical phrase",
        variant: "destructive",
      });
    }
  };

  const handleUpdate = async (id: string, updates: Partial<CanonicalPhrase>) => {
    try {
      const { error } = await supabase
        .from('canonical_phrases')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Canonical phrase updated successfully",
      });

      setEditingId(null);
      fetchPhrases();
    } catch (error) {
      console.error('Error updating canonical phrase:', error);
      toast({
        title: "Error",
        description: "Failed to update canonical phrase",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this canonical phrase?')) return;

    try {
      const { error } = await supabase
        .from('canonical_phrases')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Canonical phrase deleted successfully",
      });

      fetchPhrases();
    } catch (error) {
      console.error('Error deleting canonical phrase:', error);
      toast({
        title: "Error",
        description: "Failed to delete canonical phrase",
        variant: "destructive",
      });
    }
  };

  const handleToggleEnabled = async (id: string, enabled: boolean) => {
    await handleUpdate(id, { enabled });
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="h-16 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium">Canonical Phrases</h4>
          <p className="text-xs text-muted-foreground">
            Configure trigger phrases for "{actionTypeLabel}" actions
          </p>
        </div>
        
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline">
              <Plus className="h-3 w-3 mr-1" />
              Add Phrase
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Canonical Phrase</DialogTitle>
              <DialogDescription>
                Create a trigger phrase pattern for "{actionTypeLabel}" actions
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="phrase_pattern">Phrase Pattern</Label>
                <Textarea
                  id="phrase_pattern"
                  value={formData.phrase_pattern}
                  onChange={(e) => setFormData(prev => ({ ...prev, phrase_pattern: e.target.value }))}
                  placeholder="e.g., I'd like to schedule that"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="match_type">Match Type</Label>
                <Select value={formData.match_type} onValueChange={(value: any) => setFormData(prev => ({ ...prev, match_type: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MATCH_TYPE_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        <div>
                          <div className="font-medium">{option.label}</div>
                          <div className="text-xs text-muted-foreground">{option.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confidence_threshold">Confidence Threshold (%)</Label>
                <Input
                  id="confidence_threshold"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.confidence_threshold}
                  onChange={(e) => setFormData(prev => ({ ...prev, confidence_threshold: parseInt(e.target.value) || 80 }))}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate}>Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {phrases.map((phrase) => (
          <Card key={phrase.id} className="border-l-4 border-l-primary/20">
            <CardContent className="p-4">
              {editingId === phrase.id ? (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Phrase Pattern</Label>
                    <Textarea
                      value={phrase.phrase_pattern}
                      onChange={(e) => setPhrases(prev => 
                        prev.map(p => p.id === phrase.id ? { ...p, phrase_pattern: e.target.value } : p)
                      )}
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Match Type</Label>
                      <Select 
                        value={phrase.match_type} 
                        onValueChange={(value: any) => setPhrases(prev => 
                          prev.map(p => p.id === phrase.id ? { ...p, match_type: value } : p)
                        )}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MATCH_TYPE_OPTIONS.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Confidence Threshold (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={phrase.confidence_threshold}
                        onChange={(e) => setPhrases(prev => 
                          prev.map(p => p.id === phrase.id ? { ...p, confidence_threshold: parseInt(e.target.value) || 80 } : p)
                        )}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingId(null)}
                    >
                      <X className="h-3 w-3 mr-1" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleUpdate(phrase.id, phrase)}
                    >
                      <Save className="h-3 w-3 mr-1" />
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Badge variant="outline" className="text-xs">
                        {phrase.match_type}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        {phrase.confidence_threshold}%
                      </Badge>
                    </div>
                    <p className="text-sm font-mono bg-muted/50 p-2 rounded text-foreground/90">
                      "{phrase.phrase_pattern}"
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <Switch
                      checked={phrase.enabled}
                      onCheckedChange={(checked) => handleToggleEnabled(phrase.id, checked)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingId(phrase.id)}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(phrase.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {phrases.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <p className="text-sm text-muted-foreground text-center mb-3">
              No canonical phrases configured for this action type.
            </p>
            <Button size="sm" onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-3 w-3 mr-1" />
              Add First Phrase
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}